import {
  Box,
  MenuItem,
  Select,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Autocomplete,
  TextField,
  Tooltip,
  Tabs,
  Tab,
  Collapse,
  Chip,
  Paper,
} from '@mui/material'
import React, { useEffect, useState } from 'react'
import { serverActionService } from '../../../services/serverActionService'
import { useAuthContext } from '../../../hooks/useAuthContext'
import { DeleteRounded, ExpandMore, ExpandLess } from '@mui/icons-material'
import { toast } from 'react-toastify'
import ConfirmationDialog from '../Workflow/components/ConfirmationDialog'
import ModeSharpIcon from '@mui/icons-material/ModeSharp'
import AddOutboundServerAction from './components/AddOutboundServerAction'
import SetupKeyValuePair from './components/SetupKeyValuePair'
import { GroupHeader, GroupItems } from '../components/CreateProcessTemplateDialog'
import SetupInboundTranslation from './components/SetupInboundTranslation'
import KeyIcon from '@mui/icons-material/Key'
import ServerActionPreview from './components/ServerActionPreview'
import { useNavigate, useParams } from 'react-router-dom'
import { ACTIVITY_TYPES } from '../../../utility/constants'

export const ACTION_TYPE = {
  INBOUND: 'inbound',
  OUTBOUND: 'outbound',
}

const ServerActionConfig = () => {
  const navigate = useNavigate()
  const { type } = useParams()
  const { user } = useAuthContext()
  const [actionType, setActionType] = useState(type || ACTION_TYPE.OUTBOUND)
  const [inboundActionTranslations, setInboundActionTranslations] = useState([])
  const [outboundActions, setOutboundActions] = useState([])
  const [activities, setActivities] = useState([])
  const [routes, setRoutes] = useState([])
  const [deletingOutboundAction, setDeletingOutboundAction] = useState(null)
  const [showAddOutboundAction, setShowOutboundServerAction] = useState(false)
  const [newServerAction, setNewServerAction] = useState({ http_request_method: 'POST' })
  const [keyValuePairsForAction, showKeyValuePairsForAction] = useState(null)
  const [keyFilter, setKeyFilter] = useState({})
  const [showSetupKeyDialog, setShowSetupKeyDialog] = useState(false)
  const [expandedRow, setExpandedRow] = useState(null)
  const [activityTemplateId, setActivityTemplateId] = useState(null)

  useEffect(() => {
    ;(async () => {
      if (!user) return
      const actions = await serverActionService.getActions(user)
      setInboundActionTranslations(actions.inboundTranslations)
      setOutboundActions(actions.outbound)
      setActivities(actions.activities)
      setRoutes(actions.routes)
    })()
  }, [user, newServerAction])

  const fetchTranslations = async () => {
    const actions = await serverActionService.getInboundActionTranslations(user, activityTemplateId)
    setInboundActionTranslations(actions)
  }

  useEffect(() => {
    ;(async () => {
      if (!user || !activityTemplateId) return
      fetchTranslations()
    })()
  }, [activityTemplateId, user])

  const deleteOutboundServerAction = async (id) => {
    try {
      await serverActionService.deleteAction(user, id)
      setDeletingOutboundAction(null)
      setOutboundActions(outboundActions.filter((it) => it.id !== id))
      toast.success('Deleted successfully')
    } catch (error) {
      toast.error(`Something went wrong!`)
    }
  }

  const addOutboundServerAction = async (newServerAction) => {
    try {
      // Ensure headers and payload are objects
      const headers = newServerAction.headers || {}
      const payload = newServerAction.payload || {}

      await serverActionService.addNewAction(user, {
        ...newServerAction,
        headers,
        payload,
      })
      setShowOutboundServerAction(false)
      setNewServerAction({ http_request_method: 'POST' })
      toast.success('Saved successfully')

      // Refresh the list
      const actions = await serverActionService.getActions(user)
      setOutboundActions(actions.outbound)
    } catch (error) {
      toast.error(`Something went wrong!`)
    }
  }

  const handleEditAction = (outbound) => {
    setNewServerAction({
      ...outbound,
      headers: outbound.headers || {},
      payload: outbound.payload || {},
    })
    setShowOutboundServerAction(true)
  }

  const toggleRowExpansion = (id) => {
    setExpandedRow(expandedRow === id ? null : id)
  }

  return (
    <Box>
      <Box style={{ marginBottom: 16 }}>
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          Server Actions
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Configure HTTP requests to external APIs triggered by process activities
        </Typography>
      </Box>

      <Tabs
        value={actionType}
        onChange={(_, v) => {
          setActionType(v)
          navigate(`/admin/server-actions/${v}`)
        }}
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Outbound Actions" value={ACTION_TYPE.OUTBOUND} />
        <Tab label="Inbound Actions" value={ACTION_TYPE.INBOUND} />
      </Tabs>

      {actionType == ACTION_TYPE.OUTBOUND ? (
        <Box style={{ marginTop: 16 }}>
          <TableContainer component={Paper} style={{ backgroundColor: 'white' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width="40px"></TableCell>
                  <TableCell>Action Label</TableCell>
                  <TableCell>Trigger Activity</TableCell>
                  <TableCell>Trigger Route</TableCell>
                  <TableCell>Method | Endpoint</TableCell>
                  <TableCell align="center">Sequence</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {outboundActions?.map((outbound) => {
                  const isExpanded = expandedRow === outbound.id
                  return (
                    <React.Fragment key={outbound.id}>
                      <TableRow hover>
                        <TableCell>
                          <IconButton size="small" onClick={() => toggleRowExpansion(outbound.id)}>
                            {isExpanded ? <ExpandLess /> : <ExpandMore />}
                          </IconButton>
                        </TableCell>
                        <TableCell style={{ padding: 4 }}>
                          <Typography variant="body2" fontWeight="medium">
                            {outbound.label}
                          </Typography>
                        </TableCell>
                        <TableCell style={{ padding: 4 }}>
                          <Typography variant="body2">
                            {
                              activities.find(
                                (it) => it.id === outbound.trigger_activity_template_id,
                              )?.activity_template_name
                            }
                          </Typography>
                        </TableCell>
                        <TableCell style={{ padding: 4 }}>
                          <Typography variant="body2">
                            {routes.find((it) => it.id === outbound.trigger_route_template_id)
                              ?.route_template_name || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell style={{ padding: 4 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip
                              label={outbound.http_request_method}
                              size="small"
                              color="primary"
                            />
                            <Typography
                              variant="body2"
                              sx={{ fontSize: '0.8rem', wordBreak: 'break-all' }}
                            >
                              {outbound.endpoint}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="center" style={{ padding: 4 }}>
                          <Chip label={outbound.sequence} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell align="center" style={{ padding: 4 }}>
                          <Box style={{ display: 'flex', justifyContent: 'center' }}>
                            <Tooltip title="Delete Action">
                              <IconButton onClick={() => setDeletingOutboundAction(outbound)}>
                                <DeleteRounded style={{ fontSize: 20 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Configure Field Mappings">
                              <IconButton onClick={() => showKeyValuePairsForAction(outbound)}>
                                <KeyIcon style={{ fontSize: 20 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit Server Action">
                              <IconButton onClick={() => handleEditAction(outbound)}>
                                <ModeSharpIcon style={{ fontSize: 20 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ margin: 2 }}>
                              <ServerActionPreview serverAction={outbound} />
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  )
                })}
                {outboundActions?.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="textSecondary">
                        No outbound server actions configured yet
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Button
            size="small"
            style={{ marginTop: 16 }}
            color="primary"
            variant="contained"
            onClick={() => {
              setNewServerAction({ http_request_method: 'POST' })
              setShowOutboundServerAction(true)
            }}
          >
            Add Outbound Server Action
          </Button>
        </Box>
      ) : (
        <Box style={{ marginTop: 16 }}>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Inbound actions allow external systems to send data to our processes. Configure field
              mappings to translate incoming data keys to our process fields.
            </Typography>
          </Paper>
          <Autocomplete
            style={{ marginBottom: 8 }}
            id="select-derived-field"
            options={
              activities.filter((it) =>
                [ACTIVITY_TYPES.DATA_ENTRY, ACTIVITY_TYPES.LONG_RUNNING].includes(it.activity_type),
              ) || []
            }
            size="small"
            getOptionLabel={(item) =>
              item?.process_template_name + ' / ' + item?.activity_template_name
            }
            value={activities?.find((it) => it.id == keyFilter.activity_template_id)}
            onChange={(_, v) => {
              setKeyFilter({
                ...keyFilter,
                activity_template_id: v?.id,
              })
              setActivityTemplateId(v?.id)
            }}
            renderInput={(params) => (
              <TextField {...params} variant="outlined" label="Select Activity" color="primary" />
            )}
            groupBy={(option) => option.process_template_name}
            renderGroup={(params) => {
              return (
                <li key={params.key}>
                  <GroupHeader>{params.group}</GroupHeader>
                  <GroupItems>{params.children}</GroupItems>
                </li>
              )
            }}
          />
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Process</TableCell>
                  <TableCell>Activity</TableCell>
                  <TableCell>Field</TableCell>
                  <TableCell>Inbound Key</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {inboundActionTranslations
                  ?.filter((it) => {
                    return keyFilter.activity_template_id == it.activity_template_id
                  })
                  .map((translation) => {
                    return (
                      <TableRow key={translation.field_template_id}>
                        <TableCell style={{ padding: 4 }}>
                          {translation.process_template_name}
                        </TableCell>
                        <TableCell style={{ padding: 4 }}>
                          {translation.activity_template_name}
                        </TableCell>
                        <TableCell style={{ padding: 4 }}>
                          {translation.field_template_name}
                        </TableCell>
                        <TableCell style={{ padding: 4 }}>
                          <Chip
                            label={translation.key || 'Not mapped'}
                            size="small"
                            variant={translation.key ? 'filled' : 'outlined'}
                          />
                        </TableCell>
                      </TableRow>
                    )
                  })}
                {(!keyFilter.activity_template_id ||
                  inboundActionTranslations?.filter(
                    (it) => it.activity_template_id == keyFilter.activity_template_id,
                  ).length === 0) && (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="textSecondary">
                        {!keyFilter.activity_template_id
                          ? 'Please select an activity to view field mappings'
                          : 'No field mappings configured for this activity'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                <TableRow></TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <Button
            size="small"
            variant="contained"
            onClick={() => setShowSetupKeyDialog(true)}
            color="primary"
            style={{ marginTop: 16 }}
            disabled={!keyFilter.activity_template_id}
          >
            Setup Inbound Keys
          </Button>
        </Box>
      )}

      <ConfirmationDialog
        title={`Delete outbound action '${deletingOutboundAction?.label}'?`}
        message={'This will also delete all field mappings associated with this action.'}
        open={deletingOutboundAction != null}
        onClose={() => setDeletingOutboundAction(null)}
        onConfirm={() => deleteOutboundServerAction(deletingOutboundAction?.id)}
        reloadProcesses={() => {
          setOutboundActions(
            outboundActions?.filter((it) => it.id != deletingOutboundAction?.id) || [],
          )
        }}
      />

      <SetupKeyValuePair
        keyValuePairsForAction={keyValuePairsForAction}
        showKeyValuePairsForAction={showKeyValuePairsForAction}
      />

      <AddOutboundServerAction
        setNewServerAction={setNewServerAction}
        addOutboundServerAction={addOutboundServerAction}
        setShowOutboundServerAction={setShowOutboundServerAction}
        newServerAction={newServerAction}
        activities={activities}
        routes={routes}
        showAddOutboundAction={showAddOutboundAction}
      />

      <SetupInboundTranslation
        open={showSetupKeyDialog}
        onClose={() => setShowSetupKeyDialog(false)}
        fields={
          inboundActionTranslations?.filter(
            (it) => it.activity_template_id == keyFilter.activity_template_id,
          ) || []
        }
        onSave={fetchTranslations}
      />
    </Box>
  )
}

export default ServerActionConfig
